/**
 * Site accessibility fixes
 *
 * @package
 * @since 1.0.0
 */

/**
 * Core Button Blocks
 *
 * Task: Add [tabindex] attribute to core Button blocks.
 * Reason: Resolves issue with button focus being skipped by the keyboard.
 */
export const buttonBlocks = () => {
	const gutenbergButtons = document.querySelectorAll(
		'.wp-block-button__link'
	);

	if ( gutenbergButtons ) {
		gutenbergButtons.forEach( function ( gutenbergButton ) {
			gutenbergButton.setAttribute( 'tabindex', '0' );
		} );
	}
};

/**
 * Core Table Block
 *
 * Task: Add `scope="col"` and `scope="row"` attributes to core Table blocks.
 * Reason: Resolves issue with table headings not being read by screen readers.
 */
export const tableBlocks = () => {
	const gutenbergTable = document.querySelector( '.wp-block-table' );

	if ( gutenbergTable ) {
		const gutenbergTableHeadings = document.querySelectorAll(
			'.wp-block-table thead th'
		);
		gutenbergTableHeadings.forEach( function ( gutenbergTableHeading ) {
			gutenbergTableHeading.setAttribute( 'scope', 'col' );
		} );
		const gutenbergTableRows = document.querySelectorAll(
			'.wp-block-table tbody tr'
		);
		gutenbergTableRows.forEach( function ( gutenbergTableRow ) {
			gutenbergTableRow.setAttribute( 'scope', 'row' );
		} );
	}
};

/**
 * Search & Filter Pro
 *
 * Task: Convert filter field headings from <h4> to <p> elements, and add class.
 * Reason: Resolves issue with heading levels being skipped.
 */
export const sfHeadings = () => {
	const sfFilterHeadings = document.querySelectorAll( '.searchandfilter h4' );

	if ( sfFilterHeadings ) {
		sfFilterHeadings.forEach( function ( sfFilterHeading ) {
			const newItem = document.createElement( 'h3' );
			newItem.classList.add( 'sf-field-heading' );
			newItem.innerHTML = sfFilterHeading.innerHTML;
			sfFilterHeading.parentNode.replaceChild( newItem, sfFilterHeading );
			sfFilterHeading.remove();
		} );
	}
};

/**
 * Custom Project Step Blocks
 *
 * Task: Force the step number of the project card to be the first element in the card.
 * Reason: Resolves issue with step number not being read first by screen readers.
 */
export const updateProjectCardTabOrder = () => {
	const className = 'wp-block-wst-blocks-project-step';
	const elementCollection = document.querySelectorAll( `.${ className }` );

	if ( ! elementCollection.length ) return;

	elementCollection.forEach( ( element ) => {
		element.setAttribute( 'tabindex', '0' );

		// Step Number
		const number = element.querySelector( `.${ className }__number` );
		if ( number ) {
			element.prepend( number );
		}
	} );
};

/**
 * WP Store Locator
 *
 * Task: Add an error message to the search input for use when input is empty.
 * Reason: Resolves issue with screen reader users not knowing what to do when the search input is empty.
 */
export const wpStoreLocatorError = () => {
	const searchForm = document.querySelector(
		'.c-service-centers-locator__search-form'
	);

	if ( ! searchForm ) return;

	const searchInput = searchForm.querySelector( '#wpsl-search-input' );
	const searchButton = searchForm.querySelector( '#wpsl-search-btn' );

	if ( ! searchInput || ! searchButton ) return;

	// Add a click event listener to the search button.
	searchButton.addEventListener( 'click', function () {
		const errorMsg = searchForm.querySelector(
			'#c-service-centers-locator__search-input-error'
		);

		// Remove error messgae if already exists.
		if ( errorMsg ) {
			errorMsg.remove();
		}

		// If search input is empty, output error message.
		if ( searchInput.value === '' ) {
			searchInput.classList.add(
				'c-service-centers-locator__search-input--error'
			);
			searchInput.setAttribute(
				'aria-describedby',
				'c-service-centers-locator__search-input-error'
			);
			searchForm.insertAdjacentHTML(
				'beforeend',
				'<div id="c-service-centers-locator__search-input-error" class="c-service-centers-locator__search-input-error" role="status">Error: Please enter a valid location or Zip Code</span>'
			);
		}
	} );
};

/**
 * WP Store Locator
 *
 * Task: Add an [aria-label] attribute to all external links.
 * Reason: Resolves issue with screen reader users not knowing that some links are external.
 */
export const wpStoreLocatorExternalLinks = () => {
	const storeLocator = document.querySelector( '.c-service-centers-locator' );

	if ( ! storeLocator ) return;

	// Detect when the store locator has focus in,
	// then find all external links and add an aria-label.
	storeLocator.addEventListener( 'focusin', function () {
		// Logic to prevent the function from running multiple times.
		const checkClassName = 'aria-labels-updated';
		if ( storeLocator.classList.contains( checkClassName ) ) {
			return;
		}
		storeLocator.classList.add( checkClassName );

		const externalLinks =
			storeLocator.querySelectorAll( 'a[target="_blank"]' );

		if ( ! externalLinks.length ) return;

		externalLinks.forEach( ( element ) => {
			const linkText = element.textContent;

			// If the element is the directions button, get the store's name and add to the aria-label.
			if ( element.classList.contains( 'wpsl-directions' ) ) {
				const storeName = element
					.closest( 'li' )
					.querySelector(
						'.wpsl-store-location p strong'
					).textContent;

				element.setAttribute(
					'aria-label',
					`${ linkText } for ${ storeName } (opens in a new window/tab)`
				);
			} else {
				element.setAttribute(
					'aria-label',
					`${ linkText } (opens in a new window/tab)`
				);
			}
		} );
	} );
};
